import { useCallback, useEffect, useState } from "react";

/**
 * Hook for managing a countdown timer with proper cleanup on unmount.
 *
 * @param initialSeconds - Initial countdown value (default 0)
 * @returns [countdown, startCountdown] - Current countdown value and function to start/restart countdown
 *
 * @example
 * const [countdown, startCountdown] = useCountdown();
 *
 * // Start a 30 second countdown
 * startCountdown(30);
 *
 * // Check if countdown is active
 * if (countdown > 0) {
 *   console.log(`${countdown}s remaining`);
 * }
 */
export function useCountdown(
  initialSeconds: number = 0,
): [number, (seconds: number) => void] {
  const [countdown, setCountdown] = useState(() =>
    Math.max(0, Math.floor(initialSeconds)),
  );
  // timerKey is used to restart the interval when startCountdown is called
  const [timerKey, setTimerKey] = useState(0);

  const isActive = countdown > 0;

  // Effect handles the interval - only runs when active or timerKey changes
  useEffect(() => {
    if (!isActive) {
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [isActive, timerKey]);

  const startCountdown = useCallback((seconds: number) => {
    const validSeconds = Math.max(0, Math.floor(seconds));
    setCountdown(validSeconds);
    if (validSeconds > 0) {
      // Bump timerKey to restart the interval effect
      setTimerKey((k) => k + 1);
    }
  }, []);

  return [countdown, startCountdown];
}
